import React, { useCallback, useEffect, useState } from 'react'
import { Save } from 'lucide-react'
import { parseUTC, toLocalISODate, todayISO } from '../../../utils/dateUtils'
import { machineJobCostAPI, moneyText, refusalText } from '../api/machineJobCost'

/**
 * JOB COST FOR MACHINES COMES FROM (PLAN-30 P4) - the company setting, on Admin > Settings.
 *
 * It ships BLANK, and blank is job cost exactly as it was: a machine line is costed from its machine type's
 * job-cost figure per hour, or what a Day/Week line billed. "Each machine's own cost rate" costs a line from the
 * machine the foreman named (its Cost rate tab in Fleet), in the line's own unit, in force on the report date -
 * and a line with no such figure WAITS rather than taking the bill rate.
 *
 * DATED: a change applies to machine lines reported on or after the day it takes effect, and only to lines
 * posted after it - it re-posts nothing. The office may then re-post a date range on purpose (below), and the
 * count says first what job cost would be under each rule. Who changed it, when, from what to what, is listed.
 */
export const MACHINE_JOB_COST_CHOICES = [
  { value: '', label: "As today - the machine type's job-cost figure per hour, or what a Day/Week line billed" },
  { value: 'each_machine', label: "Each machine's own cost rate (set on the machine's Cost rate tab in Fleet)" },
]

export const NOTHING_REPOSTED =
  'Changing this re-posts nothing. It applies to machine lines posted after it whose report date is on or after ' +
  'the date it takes effect; earlier report dates keep the rule they had. To move lines already posted, re-post ' +
  'a date range below.'

const WAIT_WORDS = {
  machine_rate: 'the machine named has no cost in the line\'s unit for that day',
  type_rate: 'no machine named, and no owned machine of the type has a cost',
  rule_not_chosen: "no machine named, and 'Machine cost on estimates' is not chosen",
  rule_ask: 'no machine named, and your company asks for one on each line',
  week_line: 'a line by the week',
  no_type: 'no machine named and no machine type',
  machine_not_found: 'the machine named is not in Fleet',
  fleet_absent: "Fleet's machines could not be found",
}

const choiceLabel = (v) => (v === 'each_machine' ? "Each machine's own cost rate" : 'As today (blank)')

function MachineCostCount({ canRepost, settingOn }) {
  const [range, setRange] = useState({ date_from: '', date_to: todayISO(), project_id: '' })
  const [count, setCount] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [repost, setRepost] = useState(null)

  const params = () => ({ date_from: range.date_from, date_to: range.date_to,
                          ...(range.project_id ? { project_id: range.project_id } : {}) })

  const run = async () => {
    setBusy(true); setError(null); setRepost(null)
    try {
      const { data } = await machineJobCostAPI.count(params())
      setCount(data)
    } catch (err) {
      setError(refusalText(err, 'The count could not be made. Nothing was changed.'))
    } finally {
      setBusy(false)
    }
  }

  const doRepost = async (dryRun) => {
    setBusy(true); setError(null)
    try {
      const { data } = await machineJobCostAPI.repost({ ...params(), project_id: range.project_id || null, dry_run: dryRun })
      setRepost(data)
      if (!dryRun) await run()
    } catch (err) {
      setError(refusalText(err, 'The re-post could not be made. Nothing was changed.'))
    } finally {
      setBusy(false)
    }
  }

  const t = count?.total
  const reposted = (repost?.projects || []).reduce((n, p) => n + Number(p.reposted || 0), 0)
  const waitingAfter = (repost?.projects || []).reduce((n, p) => n + Number(p.waiting_after || 0), 0)
  const refused = (repost?.projects || []).flatMap(p => p.refused || [])

  return (
    <div className="border-t border-gray-100 pt-4 space-y-3" data-testid="machine-cost-count">
      <div>
        <h3 className="text-sm font-semibold text-gray-800">The count, before you choose</h3>
        <p className="text-xs text-gray-500">
          What machine lines reported in these dates cost the job now, and what they would cost under each rule.
          Nothing is written.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <label className="flex flex-col text-xs text-gray-600">From
          <input type="date" aria-label="Count from" value={range.date_from} max={range.date_to || undefined}
                 onChange={e => setRange(r => ({ ...r, date_from: e.target.value }))}
                 className="mt-1 border border-gray-300 rounded px-2 py-1 text-sm" />
        </label>
        <label className="flex flex-col text-xs text-gray-600">To
          <input type="date" aria-label="Count to" value={range.date_to}
                 onChange={e => setRange(r => ({ ...r, date_to: e.target.value }))}
                 className="mt-1 border border-gray-300 rounded px-2 py-1 text-sm" />
        </label>
        <label className="flex flex-col text-xs text-gray-600">Job
          <select aria-label="Count job" value={range.project_id}
                  onChange={e => setRange(r => ({ ...r, project_id: e.target.value }))}
                  className="mt-1 border border-gray-300 rounded px-2 py-1 text-sm">
            <option value="">All jobs</option>
            {(count?.jobs || []).map(j => (
              <option key={j.project_id} value={j.project_id}>{j.project_number || j.project_id}{j.project_name ? ` - ${j.project_name}` : ''}</option>
            ))}
          </select>
        </label>
        <button onClick={run} disabled={busy || !range.date_from || !range.date_to}
                className="px-3 py-1.5 rounded border border-gray-300 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
          {busy ? 'Counting…' : 'Count'}
        </button>
      </div>
      {error && <p className="text-xs text-red-700" data-testid="machine-cost-count-error">{error}</p>}
      {t && (
        <div className="text-sm space-y-2" data-testid="machine-cost-count-result">
          <table className="w-full text-sm">
            <tbody>
              <tr><td className="py-0.5 text-gray-600">Machine lines in these dates</td><td className="text-right">{t.lines}</td></tr>
              <tr><td className="py-0.5 text-gray-600">On the job&apos;s books now ({t.posted_lines} posted)</td>
                  <td className="text-right" data-testid="count-on-books">{moneyText(t, 'on_books')}</td></tr>
              <tr><td className="py-0.5 text-gray-600">Under today&apos;s rule</td>
                  <td className="text-right" data-testid="count-todays-rule">{moneyText(t, 'todays_rule')}</td></tr>
              <tr><td className="py-0.5 text-gray-600">Under each machine&apos;s own cost rate ({t.each_machine_costed} lines costed)</td>
                  <td className="text-right" data-testid="count-each-machine">{moneyText(t, 'each_machine')}</td></tr>
              <tr><td className="py-0.5 text-gray-600">Would wait for a cost rate</td>
                  <td className="text-right font-semibold" data-testid="count-waiting">{t.each_machine_waiting}</td></tr>
            </tbody>
          </table>
          {(count.waiting_reasons || []).length > 0 && (
            <ul className="text-xs text-gray-600 space-y-0.5">
              {count.waiting_reasons.map(w => (
                <li key={w.waiting_for}>· {w.lines} line{w.lines === 1 ? '' : 's'}: {WAIT_WORDS[w.waiting_for] || w.waiting_for}</li>
              ))}
            </ul>
          )}
          <p className="text-xs text-gray-500">
            Rented machines cost $0 under both rules - the vendor&apos;s invoice is their cost.
            {t.withheld_fields?.length ? ' Figures are hidden: this login is not shown bid or project money.' : ''}
          </p>
        </div>
      )}
      {canRepost && settingOn && t && (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 space-y-2" data-testid="machine-cost-repost">
          <p className="text-xs text-gray-600">
            Re-post these dates: machine lines already posted under the old rule are reversed (the original stays,
            beside its reversal) and posted again from each machine&apos;s own cost rate. A line with no rate waits.
          </p>
          <div className="flex gap-2">
            <button onClick={() => doRepost(true)} disabled={busy}
                    className="px-3 py-1.5 rounded border border-gray-300 text-xs font-medium hover:bg-white disabled:opacity-50">
              Show what a re-post would do
            </button>
            {repost?.dry_run && reposted + waitingAfter > 0 && (
              <button onClick={() => doRepost(false)} disabled={busy}
                      className="px-3 py-1.5 rounded bg-bfs-maroon text-white text-xs font-semibold hover:bg-bfs-maroon/90 disabled:opacity-50">
                Re-post these lines
              </button>
            )}
          </div>
          {repost && (
            <p className="text-xs text-gray-700" data-testid="machine-cost-repost-said">
              {repost.dry_run ? 'A re-post would move ' : 'Re-posted '}{reposted} line{reposted === 1 ? '' : 's'}
              {waitingAfter ? `; ${waitingAfter} would wait for a cost rate` : ''}
              {refused.length ? `; ${refused.length} could not be: ${refused.map(r => r.why).join(' ')}` : ''}.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default function MachineJobCostCard({ canEdit = false }) {
  const [state, setState] = useState(null)
  const [form, setForm] = useState({ value: '', effective_date: todayISO(), note: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(null)

  const load = useCallback(async () => {
    try {
      const { data } = await machineJobCostAPI.setting(todayISO())
      setState(data)
      setForm(f => ({ ...f, value: data?.value || '' }))
    } catch (err) {
      setError(refusalText(err, 'This setting could not be read.'))
    }
  }, [])

  useEffect(() => { load() }, [load])

  const save = async () => {
    setSaving(true); setError(null); setSaved(null)
    try {
      const { data } = await machineJobCostAPI.saveSetting(
        { value: form.value, effective_date: form.effective_date, note: form.note || null }, todayISO())
      setState(data)
      setSaved(`Saved: ${choiceLabel(form.value)}, for machine lines reported from ${form.effective_date}. Nothing was re-posted.`)
      setForm(f => ({ ...f, note: '' }))
    } catch (err) {
      setError(refusalText(err, 'The setting could not be saved. Nothing was changed.'))
    } finally {
      setSaving(false)
    }
  }

  const current = state?.value ?? ''
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-5" data-testid="machine-job-cost-card">
      <div>
        <h2 className="font-semibold text-gray-800">Job cost for machines comes from</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          What a daily-report machine line costs the job. Now: <strong data-testid="machine-job-cost-now">{choiceLabel(current)}</strong>
          {state?.next && <> - changing to {choiceLabel(state.next.value)} from {state.next.effective_date}</>}.
        </p>
      </div>

      <fieldset className="space-y-2">
        <legend className="sr-only">Job cost for machines comes from</legend>
        {MACHINE_JOB_COST_CHOICES.map(o => (
          <label key={o.value || 'blank'} className="flex items-start space-x-2 text-sm text-gray-700">
            <input type="radio" name="machine_job_cost" value={o.value} checked={form.value === o.value}
                   onChange={() => setForm(f => ({ ...f, value: o.value }))} disabled={!canEdit} className="mt-0.5" />
            <span>{o.label}</span>
          </label>
        ))}
      </fieldset>

      {form.value === 'each_machine' && (
        <ul className="text-xs text-gray-600 space-y-1" data-testid="machine-job-cost-rules">
          <li>· A line naming a machine costs that machine&apos;s cost in the line&apos;s own unit (per hour, or per day for a
            Day line), in force on the report date. Never converted between units.</li>
          <li>· A line naming no machine costs your company&apos;s choice under &apos;Machine cost on estimates&apos; (the most
            expensive, or the average, owned machine of the type).</li>
          <li>· With no such cost, the line waits - nothing is posted, never the bill rate - and is listed on the job and in
            Fleet until a cost rate is saved. A rented machine&apos;s line costs $0: its invoice is its cost.</li>
          <li className="text-amber-800 font-medium" data-testid="machine-job-cost-double-count">· {state?.rule}</li>
        </ul>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-xs text-gray-600">For machine lines reported from
            <input type="date" aria-label="Takes effect from" value={form.effective_date}
                   onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))}
                   className="mt-1 border border-gray-300 rounded px-2 py-1 text-sm" />
          </label>
          <label className="flex flex-col text-xs text-gray-600 flex-1 min-w-[12rem]">Why (optional)
            <input type="text" aria-label="Why" value={form.note}
                   onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                   className="mt-1 border border-gray-300 rounded px-2 py-1 text-sm" />
          </label>
          <button onClick={save} disabled={saving || !form.effective_date || form.value === current}
                  className="flex items-center space-x-2 px-5 py-2 bg-bfs-maroon text-white rounded-lg text-sm font-semibold hover:bg-bfs-maroon/90 disabled:opacity-50">
            <Save className="h-4 w-4" />
            <span>{saving ? 'Saving…' : 'Save Job Cost Setting'}</span>
          </button>
        </div>
      )}
      {!canEdit && <p className="text-xs text-gray-400">Only administrators can change this.</p>}
      <p className="text-xs text-gray-500" data-testid="machine-job-cost-nothing-reposted">{NOTHING_REPOSTED}</p>
      {saved && <p className="text-sm text-green-700" data-testid="machine-job-cost-saved">{saved}</p>}
      {error && <p className="text-sm text-red-700" data-testid="machine-job-cost-error">{error}</p>}

      {(state?.history || []).length > 0 && (
        <div data-testid="machine-job-cost-history">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Changes</h3>
          <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
            {state.history.map(h => (
              <li key={h.id}>
                {choiceLabel(h.previous_value)} → {choiceLabel(h.value)}, for lines reported from {h.effective_date};
                {' '}by {h.set_by_name || 'someone'}{h.set_at ? ` on ${toLocalISODate(parseUTC(h.set_at))}` : ''}{h.note ? ` - ${h.note}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      <MachineCostCount canRepost={canEdit} settingOn={current === 'each_machine' || state?.next?.value === 'each_machine'
        || (state?.history || []).some(h => h.value === 'each_machine')} />
    </div>
  )
}
